import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
type Difficulty = "easy" | "medium" | "hard";
type GameState = "selecting" | "playing";

const DIFFICULTY_LEVELS = {
  easy: { attempts: 10, name: "Easy" },
  medium: { attempts: 7, name: "Medium" },
  hard: { attempts: 5, name: "Hard" },
};

const generateSecretNumber = () => Math.floor(Math.random() * 100) + 1;

function App() {
  const [gameState, setGameState] = useState<GameState>("selecting");
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");

  const [secretNumber, setSecretNumber] = useState(generateSecretNumber());
  const [guess, setGuess] = useState("");
  const [message, setMessage] = useState("Make your first guess!");
  const [messageType, setMessageType] = useState<
    "info" | "error" | "success" | "low" | "high"
  >("info");
  const [attemptsRemaining, setAttemptsRemaining] = useState(
    DIFFICULTY_LEVELS[difficulty].attempts
  );
  const [gameOver, setGameOver] = useState(false);
  const startGame = useCallback(() => {
    const newSecretNumber = generateSecretNumber();
    const newAttempts = DIFFICULTY_LEVELS[difficulty].attempts;

    setSecretNumber(newSecretNumber);
    setAttemptsRemaining(newAttempts);
    setGuess("");
    setMessage("Make your first guess!");
    setMessageType("info");
    setGameOver(false);
  }, [difficulty]);

  useEffect(() => {
    if (gameState === "playing") {
      startGame();
    }
  }, [gameState, startGame]);

  const handleDifficultySelect = (level: Difficulty) => {
    setDifficulty(level);
    setGameState("playing");
  };

  const handlePlayAgain = () => {
    setGameState("selecting");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (gameOver) return;

    const numericGuess = parseInt(guess, 10);

    if (isNaN(numericGuess) || numericGuess < 1 || numericGuess > 100) {
      setMessage("Please enter a valid number between 1 and 100.");
      setMessageType("error");
      setGuess("");
      return;
    }

    const newAttemptsRemaining = attemptsRemaining - 1;
    setAttemptsRemaining(newAttemptsRemaining);
    if (numericGuess === secretNumber) {
      setMessage(`🎉 Correct! The number was ${secretNumber}.`);
      setMessageType("success");
      setGameOver(true);
    } else if (newAttemptsRemaining === 0) {
      setMessage(`😢 Game Over! The correct number was ${secretNumber}.`);
      setMessageType("error");
      setGameOver(true);
    } else {
      setMessageType(numericGuess < secretNumber ? "low" : "high");
      setMessage(
        numericGuess < secretNumber
          ? "Too Low! Try a higher number."
          : "Too High! Try a lower number."
      );
    }

    setGuess("");
  };

  const handleGuessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuess(e.target.value);
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Number Guesser</h1>
      </header>

      <main className="App-content">
        {}

        {gameState === "selecting" && (
          <div className="game-card difficulty-selector">
            <h2>Select Your Difficulty</h2>
            <p>Choose a level to start the game.</p>
            <div className="difficulty-buttons">
              <button onClick={() => handleDifficultySelect("easy")}>
                <strong>Easy</strong>
              </button>
              <button onClick={() => handleDifficultySelect("medium")}>
                <strong>Medium</strong>
              </button>
              <button onClick={() => handleDifficultySelect("hard")}>
                <strong>Hard</strong>
              </button>
            </div>
          </div>
        )}

        {gameState === "playing" && (
          <div className="game-card game-screen">
            <div className="game-header">
              <h2>Guess the Number!</h2>
              <span className="game-difficulty">
                {DIFFICULTY_LEVELS[difficulty].name} Level
              </span>
            </div>

            <p className="attempts-remaining">
              Attempts Remaining: <strong>{attemptsRemaining}</strong>
            </p>

            <p className={`message message-${messageType}`}>{message}</p>

            <form onSubmit={handleSubmit} className="guess-form">
              <input
                type="number"
                value={guess}
                onChange={handleGuessChange}
                placeholder="Enter your guess (1-100)"
                disabled={gameOver}
                className="guess-input"
                min="1"
                max="100"
                autoFocus
              />
              <button
                type="submit"
                disabled={gameOver}
                className="guess-button"
              >
                Guess
              </button>
            </form>

            {gameOver && (
              <button onClick={handlePlayAgain} className="restart-button">
                Play Again?
              </button>
            )}
          </div>
        )}
      </main>

      <footer className="App-footer">
        <p>© 2025 Game</p>
      </footer>
    </div>
  );
}

export default App;
